import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Field, Input, Select } from '../components/form';
import { Alert, Badge, Button, Card, EmptyState, Spinner } from '../components/ui';
import styles from './screens.module.css';

interface Usuario {
  id: number;
  email: string;
  papel: string;
  pessoaId: number | null;
  ativo: boolean;
}
interface Pessoa {
  id: number;
  nome: string;
  email: string | null;
}
interface RespostaApi {
  emailEnviado?: boolean;
  error?: string;
}

type Carga =
  | { estado: 'carregando' }
  | { estado: 'erro' }
  | { estado: 'ok'; usuarios: Usuario[]; pessoas: Pessoa[] };

type Aviso = { kind: 'success' | 'warning' | 'danger'; texto: string };
type Campo = 'pessoa' | 'email' | 'papel';

/** Papéis que a API aceita no convite (`USUARIO_PAPEIS`) e seus rótulos. */
const PAPEL_LABEL: Record<string, string> = {
  morador: 'Morador',
  proprietario: 'Proprietário',
  contabil: 'Contábil',
  sindico: 'Síndico',
};
const PAPEIS = Object.keys(PAPEL_LABEL);

const MSG_PESSOA = 'Escolha a pessoa.';
const MSG_EMAIL = 'Informe um e-mail válido.';

/** Erros de validação da API que pertencem a um campo do formulário. */
const ERRO_DE_CAMPO: Record<string, { campo: Campo; mensagem: string }> = {
  email_duplicado: { campo: 'email', mensagem: 'Esse e-mail já está em uso por outro usuário.' },
  invalid_email: { campo: 'email', mensagem: MSG_EMAIL },
  invalid_papel: { campo: 'papel', mensagem: 'Escolha o papel.' },
  pessoa_not_found: { campo: 'pessoa', mensagem: 'Pessoa não encontrada. Recarregue a página.' },
  invalid_pessoa: { campo: 'pessoa', mensagem: MSG_PESSOA },
};

/** Demais erros da API (código → mensagem), mostrados acima da lista. */
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  not_found: 'Usuário não encontrado ou inativo. A lista foi atualizada.',
};
const ERRO_GERAL_PADRAO = 'Não foi possível concluir. Tente novamente.';

/**
 * Usuários do condomínio (RF-A04): convite por e-mail, ativar/desativar e reset de
 * senha. `editavel` só para o síndico de condomínio ativo: POST/PATCH/reset são
 * `requireRole('sindico')`, o contábil é somente leitura e o tenant suspenso também.
 * A lista da API não traz nomes (nem hash ou token): a tela cruza com /api/pessoas.
 */
export function Usuarios({ editavel }: { editavel: boolean }) {
  const { user } = useAuth();
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  const [aviso, setAviso] = useState<Aviso | null>(null);

  const carregar = useCallback(async () => {
    const [u, p] = await Promise.all([
      getJson<{ usuarios: Usuario[] }>('/api/usuarios'),
      getJson<{ pessoas: Pessoa[] }>('/api/pessoas'),
    ]);
    setCarga(
      u && p ? { estado: 'ok', usuarios: u.usuarios, pessoas: p.pessoas } : { estado: 'erro' },
    );
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /** Erro da API fora dos campos; usuário inexistente/inativo recarrega a lista. */
  async function tratarFalha(codigo?: string) {
    setAviso({ kind: 'danger', texto: (codigo && ERRO_GERAL[codigo]) || ERRO_GERAL_PADRAO });
    if (codigo === 'not_found') await carregar();
  }

  async function alternarAtivo(u: Usuario) {
    setAviso(null);
    const res = await mutateJson<RespostaApi>(
      `/api/usuarios/${encodeURIComponent(u.id)}`,
      { ativo: !u.ativo },
      'PATCH',
    );
    if (res.ok) await carregar();
    else await tratarFalha(res.data?.error);
  }

  async function redefinirSenha(u: Usuario) {
    setAviso(null);
    const res = await mutateJson<RespostaApi>(
      `/api/usuarios/${encodeURIComponent(u.id)}/senha/reset`,
      {},
    );
    if (!res.ok) {
      await tratarFalha(res.data?.error);
      return;
    }
    setAviso(
      res.data?.emailEnviado
        ? { kind: 'success', texto: `Link para redefinir a senha enviado a ${u.email}.` }
        : {
            kind: 'warning',
            texto:
              'O e-mail com o link não foi enviado (provedor indisponível ou não configurado). Tente de novo mais tarde.',
          },
    );
  }

  async function aoConvidar(email: string, emailEnviado: boolean) {
    setAviso(
      emailEnviado
        ? { kind: 'success', texto: `Convite enviado para ${email}.` }
        : {
            kind: 'warning',
            texto:
              'Usuário criado, mas o e-mail de convite não saiu. Use "Redefinir senha" para reenviar o link quando o e-mail estiver disponível.',
          },
    );
    await carregar();
  }

  if (carga.estado === 'carregando') return <Spinner />;
  if (carga.estado === 'erro') {
    return <Alert kind="danger">Não foi possível carregar os usuários.</Alert>;
  }

  const { usuarios, pessoas } = carga;
  const nomePessoa = (id: number | null) =>
    (id !== null && pessoas.find((p) => p.id === id)?.nome) || '—';
  return (
    <div className={styles.stack}>
      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}

      {editavel ? (
        <ConviteForm pessoas={pessoas} onConvidado={aoConvidar} onErroGeral={tratarFalha} />
      ) : null}

      {usuarios.length === 0 ? (
        <EmptyState>Nenhum usuário cadastrado.</EmptyState>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Pessoa</th>
                <th>E-mail</th>
                <th>Papel</th>
                <th>Situação</th>
                {editavel ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id}>
                  <td>{nomePessoa(u.pessoaId)}</td>
                  <td>{u.email}</td>
                  <td>{PAPEL_LABEL[u.papel] ?? u.papel}</td>
                  <td>
                    <Badge tone={u.ativo ? 'success' : 'warning'}>
                      {u.ativo ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  {editavel ? (
                    <td>
                      <div className={styles.row}>
                        {u.ativo ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            aria-label={`Redefinir senha de ${u.email}`}
                            onClick={() => void redefinirSenha(u)}
                          >
                            Redefinir senha
                          </Button>
                        ) : null}
                        {u.id === user?.id ? null : (
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`${u.ativo ? 'Desativar' : 'Ativar'} usuário ${u.email}`}
                            onClick={() => void alternarAtivo(u)}
                          >
                            {u.ativo ? 'Desativar' : 'Ativar'}
                          </Button>
                        )}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Convite a partir de uma pessoa cadastrada; erros de validação vão para o campo. */
function ConviteForm({
  pessoas,
  onConvidado,
  onErroGeral,
}: {
  pessoas: Pessoa[];
  onConvidado: (email: string, emailEnviado: boolean) => Promise<void>;
  /** Erro da API fora dos campos (código; ausente = falha de rede). */
  onErroGeral: (codigo?: string) => Promise<void>;
}) {
  const [pessoaId, setPessoaId] = useState('');
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState(PAPEIS[0] ?? '');
  const [erros, setErros] = useState<Partial<Record<Campo, string>>>({});
  const [enviando, setEnviando] = useState(false);

  function escolherPessoa(valor: string) {
    setPessoaId(valor);
    // Sugere o e-mail cadastrado na pessoa; o síndico pode trocá-lo.
    setEmail(pessoas.find((p) => p.id === Number(valor))?.email ?? '');
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    const locais: Partial<Record<Campo, string>> = {};
    if (!pessoaId) locais.pessoa = MSG_PESSOA;
    if (!email.includes('@')) locais.email = MSG_EMAIL;
    setErros(locais);
    if (locais.pessoa || locais.email) return;

    setEnviando(true);
    const res = await mutateJson<RespostaApi>('/api/usuarios', {
      pessoaId: Number(pessoaId),
      email: email.trim(),
      papel,
    });
    setEnviando(false);

    if (res.ok) {
      const enviado = email.trim().toLowerCase();
      setPessoaId('');
      setEmail('');
      await onConvidado(enviado, res.data?.emailEnviado === true);
      return;
    }
    const codigo = res.data?.error;
    const deCampo = codigo ? ERRO_DE_CAMPO[codigo] : undefined;
    if (deCampo) setErros({ [deCampo.campo]: deCampo.mensagem });
    else await onErroGeral(codigo);
  }

  return (
    <Card title="Convidar usuário">
      <form onSubmit={enviar} noValidate>
        <div className={styles.row}>
          <Field label="Pessoa" error={erros.pessoa}>
            {(id) => (
              <Select id={id} value={pessoaId} onChange={(e) => escolherPessoa(e.target.value)}>
                <option value="">Escolha…</option>
                {pessoas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="E-mail" error={erros.email}>
            {(id) => (
              <Input
                id={id}
                type="email"
                value={email}
                maxLength={254}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Field label="Papel" error={erros.papel}>
            {(id) => (
              <Select id={id} value={papel} onChange={(e) => setPapel(e.target.value)}>
                {PAPEIS.map((p) => (
                  <option key={p} value={p}>
                    {PAPEL_LABEL[p]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Button type="submit" disabled={enviando}>
          Enviar convite
        </Button>
      </form>
    </Card>
  );
}
